import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Formik, Field, Form, FormikProps } from "formik";
import * as yup from "yup";
import { request } from "../../../../../utils/Request";
import { Col } from "react-bootstrap";
import { InputField } from "../../../../../components/Shared/InputField";
import { Sidebar } from "primereact/sidebar";
import { get, includes } from "lodash";
import { Button } from "primereact/button";

interface IFormProps {
    t?: any | undefined;
    params: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    id?: any;
    title?: string | null;
};

const DEFAULT_FORM_STATE: TInputForm = {
    id: null,
    title: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        title: yup
            .string()
            .trim(t("the field is invalid."))
            .required(t("the field is required.", { field: t("title") }))
            .nullable(),
    });
};

function ProviderForm(props: IFormProps) {
    const { t, toastify, params } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const onClose = () => {
        setPanelState(false);
        const base: any = formRef.current;
        if (base) {
            base.resetForm();
        }
        if (props.onClose) {
            props.onClose(!includes(["edit"], get(params, ["operation"])));
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            await request({
                method: !payloads.id ? "POST" : "PATCH",
                url: !payloads.id ? "/suppliers/settings/providers" : `/suppliers/settings/providers/${ payloads.id }`,
                data: { ...payloads },
            });

            toastify(
                payloads.id
                    ? t("record updated", { item: t("provider") })
                    : t("record added", { item: t("provider") }),
                payloads.id ? "success" : "info"
            );

            resetForm();
            onClose();
        } catch (e: any) {
            if (e.status === 422) {
                setErrors(e.data.errors);
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onEdit = async (id: string) => {
        try {
            const response = await request({ url: `/suppliers/settings/providers/${ id }` });
            const { data } = response.data;
            setInitFormState({ ...data });
            setPanelState(true);
        } catch (e: any) {
            toastify(t("server error"), "error");
        }
    };

    useEffect(() => {
        if (includes(["add"], get(params, ["operation"]))) {
            setInitFormState(DEFAULT_FORM_STATE);
            setPanelState(true);
        }
        if (includes(["edit"], get(params, ["operation"]))) {
            onEdit(params.operationId);
        }
    }, [params]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0" }>
                        {includes(["edit"], get(params, ["operation"])) ? t("edit provider") : t("add provider")}
                    </h5>
                </div>
            }
            visible={ panelState }
            position="right"
            onHide={ onClose }
            blockScroll={ true }
            style={ { width: "450px" } }
            closeOnEscape={ false }
        >
            <div
                style={ {
                    height: "3px",
                    background: "linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)",
                } }
            ></div>
            <Formik
                innerRef={ formRef }
                enableReinitialize={ true }
                initialValues={ initFormState }
                validationSchema={ validationSchema(t) }
                onSubmit={ onSubmit }
            >
                {(formik: FormikProps<TInputForm>) => (
                    <Form onSubmit={ formik.handleSubmit }>
                        <Col sm={ 12 } className={ "mt-3" }>
                            <Field component={ InputField } name="title" label={ t("title") } placeholder={ t("title") } />
                        </Col>
                        <div className="filter-card-footer" style={ { width: "420px" } }>
                            <Button type={ "submit" } disabled={ formik.isSubmitting } size={ "small" } outlined>
                                <i className="pi pi-save me-2" />
                                { t("save") }
                            </Button>
                            <Button
                                type={ "button" }
                                disabled={ formik.isSubmitting }
                                onClick={ onClose }
                                className={ "ms-2" }
                                size={ "small" }
                                severity={ "warning" }
                                outlined
                            >
                                <i className="pi pi-arrow-left me-2" />
                                { t("cancel") }
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Sidebar>
    );
}

export default withTranslation()(ProviderForm);
